import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { MdCelebration, MdArrowCircleUp } from 'react-icons/md';
import { WorkspaceT, hasNewVersion, TGVersion, isDeprecated } from '../../type';
import { Button } from '@tigergraph/app-ui-lib/button';
import { useState } from 'react';
import UpgradeVersionModal from './UpgradeVersionModal';
import { useUpgradeVersionHandler } from '@/pages/workgroup/hook/useUpgradeVersionHandler';

export function DeprecatedBanner({
  workspace,
  versions,
  onScheduleUpgrade,
}: {
  workspace: WorkspaceT;
  versions: TGVersion[];
  onScheduleUpgrade: () => void;
}) {
  const [css, theme] = useStyletron();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { handleUpgradeConfirm, isLoading: upgrading } = useUpgradeVersionHandler();

  if (!isDeprecated(workspace, versions)) {
    return null;
  }

  const onConfirm = ({ version, timing }: { version: string; timing: 'now' | 'schedule' }) => {
    setShowUpgradeModal(false);
    handleUpgradeConfirm(
      workspace,
      { version, timing },
      {
        onScheduleSuccess: onScheduleUpgrade,
      }
    );
  };

  return (
    <>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          backgroundColor: theme.colors['background.danger.subtlest'],
        })}
      >
        <div className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}>
          <MdArrowCircleUp size={32} color={theme.colors['icon.danger']} />
          <span className={css({ fontWeight: 600 })}>The TigerGraph version for this workspace is deprecated.</span>
        </div>
        <Button kind="secondary" disabled={upgrading} onClick={() => setShowUpgradeModal(true)}>
          Upgrade Now
        </Button>
      </div>

      <UpgradeVersionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={onConfirm}
        versions={versions}
        desiredTgVersion={workspace.desired_tg_version}
        workspace={workspace}
      />
    </>
  );
}

export function VersionBanner({
  workspace,
  versions,
  onScheduleUpgrade,
}: {
  workspace: WorkspaceT;
  versions: TGVersion[];
  onScheduleUpgrade: () => void;
}) {
  const [css, theme] = useStyletron();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { handleUpgradeConfirm, isLoading: upgrading } = useUpgradeVersionHandler();

  // prefer to show the deprecated banner
  if (isDeprecated(workspace, versions) || !hasNewVersion(workspace, versions)) {
    return null;
  }

  const onConfirm = ({ version, timing }: { version: string; timing: 'now' | 'schedule' }) => {
    setShowUpgradeModal(false);
    handleUpgradeConfirm(
      workspace,
      { version, timing },
      {
        onScheduleSuccess: onScheduleUpgrade,
      }
    );
  };

  return (
    <>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          backgroundColor: theme.colors['background.warning.subtlest'],
        })}
      >
        <div className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}>
          <MdCelebration size={32} color={theme.colors['icon.warning']} />
          <span className={css({ fontWeight: 600 })}>New TigerGraph Version Available</span>
        </div>
        <Button kind="secondary" disabled={upgrading} onClick={() => setShowUpgradeModal(true)}>
          Upgrade Now
        </Button>
      </div>
      <UpgradeVersionModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={onConfirm}
        versions={versions}
        desiredTgVersion={workspace.desired_tg_version}
        workspace={workspace}
      />
    </>
  );
}
